 
import React, { useEffect, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    ColumnDef,
    flexRender,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    NSTable,
    NSCellInput,
    NSSelect,
} from 'bricks';
import { customStyles } from 'bricks/NSSelect/NSSelect';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import RenderIf from 'components/RenderIf/RenderIf';
import { ProFormaModelIds, IncomeCalculationType, PRO_FORMA_MODELS_WITH_LEASING_COST_RESERVES } from 'constants/proForma';
import {
    OPERATIONS_EXPENSES_WS_HANDLERS, OperationExpenseType, WattsType,
} from 'views/ProFormaTable/constants';
import { OperatingExpense } from 'views/ProFormaTable/types';
import { useProFormaSocketContext } from '../../../socketContext/ProFormaSocketProvider';
import {
    ColumnKeys, COLUMN_KEYS_BY_MODEL_ID, CAPITAL_RESERVES_OPTIONS, CAPITAL_RESERVES_OPTION_VALUES,
} from '../constants';
import './OperatingExpensesTable.scss';

export interface IOperatingExpensesTableProps {
    operationBeginIndex: number;
    baseCashFlowColumns: ColumnDef<any>[];
}

const OperatingExpensesTable = ({ operationBeginIndex, baseCashFlowColumns }: IOperatingExpensesTableProps) => {
    const {
        expenses, handleCreateOperationsRow, handleUpdateOperationsRow, handleDeleteOperationsRow,
        showCents, formatDisplayValue, operationsCashFlow, proForma, operatingInfo, wattsType,
    } = useProFormaSocketContext();

    const isApartment = [ProFormaModelIds.APARTMENT, ProFormaModelIds.APARTMENT_WITH_RETAIL].includes(proForma.proFormaModelId);
    const isCommercial = PRO_FORMA_MODELS_WITH_LEASING_COST_RESERVES.includes(proForma.proFormaModelId);
    const isIncomeCalculationTypeEstimate = operatingInfo?.incomeCalculationType === IncomeCalculationType.ESTIMATE;
    const areaType = isApartment || (isIncomeCalculationTypeEstimate && !isCommercial) ? 'SF' : 'RSF';

    const [operatingExpenses, setOperatingExpenses] = useState<any[]>([]);

    useEffect(() => {
        if (expenses) {
            const updatedOperatingExpenses = [...(expenses.operatingExpenses || [])];
            if (expenses.propertyTaxesOperatingExpense) updatedOperatingExpenses.push(expenses.propertyTaxesOperatingExpense);
            setOperatingExpenses(updatedOperatingExpenses);
        }
    }, [expenses]);

    const handleUpdateOperatingExpense = (id: number, field: string) => handleUpdateOperationsRow(
        OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.UPDATE, id, field,
    );

    const handleDeleteOperatingExpense = (id: number) => handleDeleteOperationsRow(
        OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.DELETE, id,
    );

    const cashFlowColumns: ColumnDef<OperatingExpense>[] = baseCashFlowColumns.map((column, cashFlowIndex) => ({
        ...column,
        id: `operating-expense-${column.id}`,
        accessorFn: (row, rowIndex) => {
            if (!operationBeginIndex) return null;

            if (row.operationExpenseType === OperationExpenseType.PROPERTY_TAXES) {
                return typeof operationsCashFlow?.[operationBeginIndex]?.propertyTaxesOperatingExpense?.value === 'number'
                    ? (operationsCashFlow?.[cashFlowIndex]?.propertyTaxesOperatingExpense?.value || 0)
                    : null;
            }
            return typeof operationsCashFlow?.[operationBeginIndex]?.operatingExpenses?.[rowIndex]?.value === 'number'
                ? (operationsCashFlow?.[cashFlowIndex]?.operatingExpenses?.[rowIndex]?.value || 0)
                : null;
        },
        footer: () => (
            <RenderIf isTrue={operationBeginIndex}>
                <span className="text-dark">
                    {formatDisplayValue(operationsCashFlow?.[cashFlowIndex]?.totalOperatingExpenses || 0, showCents, 'currency')}
                </span>
            </RenderIf>
        ),
    }));

     
    const COLUMNS_BY_KEY: { [key in ColumnKeys]: ColumnDef<OperatingExpense> } = {
        [ColumnKeys.ANNUAL_GROWTH]: {
            accessorKey: 'annualGrowth',
            id: 'annualGrowth',
            header: () => <span>Annual growth</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'annualGrowth');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalOperatingExpenses?.totalAnnualGrowth || 0, true, 'percentage')}</span>,
        },
        [ColumnKeys.PERCENT_OF_EGR]: {
            accessorKey: 'percentOfEffectiveGrossRevenue',
            id: 'percentOfEffectiveGrossRevenue',
            header: () => <span>% of effective gross revenue</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    type="number"
                    numberType="percentage"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'percentOfEffectiveGrossRevenue');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter percentage"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalOperatingExpenses?.totalPercentOfEffectiveGrossRevenue || 0, true, 'percentage')}</span>,
        },
        [ColumnKeys.COST_PER_UNIT_PER_YEAR]: {
            accessorKey: 'costPerUnitPerYear',
            id: 'costPerUnitPerYear',
            header: () => <span>$/unit/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'costPerUnitPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalOperatingExpenses?.totalCostPerUnitPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_SF_PER_YEAR]: {
            accessorKey: 'valuePerSfPerYear',
            id: 'valuePerSfPerYear',
            header: () => <span>{`$/${areaType}/year`}</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'valuePerSfPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalOperatingExpenses?.totalValuePerSfPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_RACK_PER_YEAR]: {
            accessorKey: 'valuePerRackPerYear',
            id: 'valuePerRackPerYear',
            header: () => <span>$/rack/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'valuePerRackPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalOperatingExpenses?.totalValuePerRackPerYear || 0, true, 'currency')}</span>,
        },
        [ColumnKeys.VALUE_PER_KW_PER_YEAR]: wattsType === WattsType.MEGAWATTS ? {
            accessorKey: 'valuePerMwPerYear',
            id: 'valuePerMwPerYear',
            header: () => <span>$/MW/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'valuePerMwPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalOperatingExpenses?.totalValuePerMwPerYear || 0, true, 'currency')}</span>,
        } : {
            accessorKey: 'valuePerKwPerYear',
            id: 'valuePerKwPerYear',
            header: () => <span>$/kW/year</span>,
            cell: ({ row, getValue }) => (
                <NSCellInput
                    showCents
                    type="number"
                    numberType="currency"
                    value={getValue()}
                    handleUpdateValue={updatedValue => {
                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'valuePerKwPerYear');
                        if (typeof updateFn === 'function') {
                            updateFn({
                                target: { value: updatedValue },
                            } as React.ChangeEvent<HTMLInputElement>);
                        }
                    }}
                    isRightAligned
                    placeholder="Enter amount"
                />
            ),
            footer: () => <span>{formatDisplayValue(expenses.totalOperatingExpenses?.totalValuePerKwPerYear || 0, true, 'currency')}</span>,
        },
    };

    const operatingExpensesColumns: ColumnDef<any>[] = [
        {
            accessorKey: 'name',
            id: 'name',
            header: () => (
                <div className="d-flex align-items-center justify-content-between w-100 px-1">
                    <span>Operating expenses</span>
                    <FontAwesomeIcon
                        id="plus-icon"
                        icon={faPlus}
                        className="ml-2 text-muted cursor--pointer"
                        onClick={() => handleCreateOperationsRow(OPERATIONS_EXPENSES_WS_HANDLERS.OPEX.CREATE)}
                    />
                </div>
            ),
            cell: ({ row, getValue }) => (
                <div className="d-flex align-items-center justify-content-between w-100 pr-1">
                    <div className="w-100">
                        <div className="d-flex justify-content-between">
                            <div className="w-100">
                                <NSCellInput
                                    type="text"
                                    value={getValue()}
                                    handleUpdateValue={updatedValue => {
                                        const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'name');
                                        if (typeof updateFn === 'function') {
                                            updateFn({
                                                target: { value: updatedValue },
                                            } as React.ChangeEvent<HTMLInputElement>);
                                        }
                                    }}
                                    placeholder="Enter name"
                                    disabled={row.original.operationExpenseType !== OperationExpenseType.STANDARD}
                                    labelClassName={row.original.operationExpenseType !== OperationExpenseType.STANDARD ? 'text-dark' : ''}
                                />
                            </div>
                            <RenderIf isTrue={row.original.operationExpenseType === OperationExpenseType.PROPERTY_TAXES}>
                                <div className="d-flex align-items-center">
                                    <span className="text-dark text-nowrap">
                                        Mill rate:
                                    </span>
                                    <NSCellInput
                                        showCents
                                        type="number"
                                        numberType="percentage"
                                        value={row.original.millRate || 0}
                                        handleUpdateValue={updatedValue => {
                                            const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'millRate');
                                            if (typeof updateFn === 'function') {
                                                updateFn({
                                                    target: { value: updatedValue },
                                                } as React.ChangeEvent<HTMLInputElement>);
                                            }
                                        }}
                                        isRightAligned
                                        inputWidth="50px"
                                    />
                                </div>
                            </RenderIf>
                        </div>
                        <RenderIf isTrue={row.original.operationExpenseType === OperationExpenseType.CAPITAL_RESERVES}>
                            <div className="pb-1">
                                <NSSelect
                                    name={`expense-table-select-${row.id}`}
                                    options={CAPITAL_RESERVES_OPTIONS}
                                    value={CAPITAL_RESERVES_OPTIONS.find(option => option.value === CAPITAL_RESERVES_OPTION_VALUES.OPEX)}
                                    onChange={option => {
                                        if (option && option?.value === CAPITAL_RESERVES_OPTION_VALUES.CAPEX) {
                                            handleDeleteOperatingExpense(Number(row.original.id));
                                        }
                                    }}
                                    selectedItemPrefix="From: "
                                    isClearable={false}
                                    menuPosition="fixed"
                                    menuPortalTarget={document.body}
                                    styles={{
                                        ...customStyles,
                                        control: (provided: any) => ({
                                            ...provided,
                                            backgroundColor: '#4F5B69',
                                            marginTop: '4px',
                                            border: 'none',
                                            minHeight: '28px',
                                        }),
                                    }}
                                />
                            </div>
                        </RenderIf>
                    </div>
                    <RenderIf isTrue={row.original.operationExpenseType === OperationExpenseType.STANDARD}>
                        <FontAwesomeIcon
                            id="trash-icon"
                            icon={faTrash}
                            className="ml-1 text-dark cursor--pointer"
                            onClick={() => handleDeleteOperatingExpense(Number(row.original.id))}
                        />
                    </RenderIf>
                </div>
            ),
            footer: () => <span className="px-1">Total operating expenses</span>,
        },
        ...(COLUMN_KEYS_BY_MODEL_ID[proForma.proFormaModelId] || []).map(key => COLUMNS_BY_KEY[key]),
        {
            accessorKey: 'totalAmountPerYear',
            id: 'totalAmountPerYear',
            header: () => <span className="pr-1">Amount/year</span>,
            cell: ({ row, getValue }) => (
                <div className="pr-1">
                    <NSCellInput
                        showCents={showCents}
                        type="number"
                        numberType="currency"
                        value={getValue()}
                        handleUpdateValue={updatedValue => {
                            const updateFn = handleUpdateOperatingExpense(Number(row.original.id), 'totalAmountPerYear');
                            if (typeof updateFn === 'function') {
                                updateFn({
                                    target: { value: updatedValue },
                                } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        isRightAligned
                        placeholder="Enter amount"
                    />
                </div>
            ),
            footer: () => (
                <span className="pr-1">
                    {formatDisplayValue(expenses.totalOperatingExpenses?.totalAmountPerYear || 0, showCents, 'currency')}
                </span>
            ),
        },
        ...cashFlowColumns,
    ];

    const table = useReactTable({
        data: operatingExpenses,
        columns: operatingExpensesColumns,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
    });

    return (
        <NSTable className={`NSTable OperatingExpensesTable--${proForma.proFormaModelName?.replace(/ /g, '-')} mb-1`}>
            <thead className="NSTable__thead">
                {table.getHeaderGroups().map(headerGroup => (
                    <tr key={headerGroup.id} className="NSTable__thead__tr">
                        {headerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__thead__tr__th p-1">
                                {header.isPlaceholder ? null : (
                                    <div>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext(),
                                        )}
                                    </div>
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </thead>
            <tbody
                className={classNames('NSTable__tbody', {
                    'NSTable__tbody--pro-forma-placeholder': !table.getRowModel().rows.length,
                })}
            >
                {table.getRowModel().rows.map(row => (
                    <tr key={row.id} className="NSTable__tbody__tr NSTable__tbody__tr--level-1">
                        {row.getVisibleCells().map(cell => (
                            <td key={cell.id} className="NSTable__tbody__tr__td p-1">
                                {flexRender(
                                    cell.column.columnDef.cell,
                                    cell.getContext(),
                                )}
                            </td>
                        ))}
                    </tr>
                ))}
            </tbody>
            <tfoot className="NSTable__tfoot">
                {table.getFooterGroups().map(footerGroup => (
                    <tr key={footerGroup.id} className="NSTable__tfoot__tr">
                        {footerGroup.headers.map(header => (
                            <th key={header.id} className="NSTable__tfoot__tr__th p-1">
                                {flexRender(
                                    header.column.columnDef.footer,
                                    header.getContext(),
                                )}
                            </th>
                        ))}
                    </tr>
                ))}
            </tfoot>
        </NSTable>
    );
};

export default OperatingExpensesTable;
